/**
 * Boolean input — two-button "Yes / No" group (Step 5).
 *
 * Selected option gets a colored ring + filled background. Clicking the same
 * choice again clears the value (returns to `null`) so users can un-set a
 * mistakenly-selected answer in non-required fields.
 */
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import type { InputProps } from '../input-props'

// Defensive read — historical data may store true_label/false_label either as
// a plain string or as a LocalizedString `{fa: '...'}` object.
function readLabel(v: unknown): string | undefined {
  if (v == null) return undefined
  if (typeof v === 'string') return v
  if (typeof v === 'object') return (v as { fa?: string }).fa
  return undefined
}

export function BooleanInput({ field, value, onChange, onBlur, disabled, invalid }: InputProps) {
  const tc = field.type_config ?? {}
  const yesLabel = readLabel(tc.true_label)  || 'بله'
  const noLabel  = readLabel(tc.false_label) || 'خیر'

  const set = (next: boolean) => {
    if (disabled) return
    // Toggle off when the same option is re-clicked.
    onChange(value === next ? null : next)
  }

  return (
    <div className="flex items-center gap-2" onBlur={onBlur}>
      <BoolButton selected={value === true}  onClick={() => set(true)}  disabled={disabled} invalid={invalid} tone="yes">{yesLabel}</BoolButton>
      <BoolButton selected={value === false} onClick={() => set(false)} disabled={disabled} invalid={invalid} tone="no" >{noLabel} </BoolButton>
    </div>
  )
}

function BoolButton({
  selected, onClick, disabled, invalid, tone, children,
}: {
  selected: boolean
  onClick: () => void
  disabled?: boolean
  invalid?: boolean
  tone: 'yes' | 'no'
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'flex flex-1 min-w-[5rem] items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium',
        'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        selected
          ? tone === 'yes'
            ? 'border-emerald-500 bg-emerald-500/25 text-emerald-200 ring-1 ring-emerald-500'
            : 'border-red-500 bg-red-500/20 text-red-200 ring-1 ring-red-500'
          : 'border-border bg-background text-foreground hover:border-foreground/40',
        invalid && !selected && 'border-destructive/50',
      )}
    >
      {selected && <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />}
      {children}
    </button>
  )
}
